// File: settingspage.js
//
// The user's profile / account settings page.
// Lets a signed-in user edit their first name, last name, phone number,
// and newsletter preference. Saving writes those fields back to their
// document in Firestore (users/<their uid>).
//
// Sections in this file:
//   - Icons             (small SVG icons used next to each field)
//   - SettingsField     (helper that renders one labeled input row)
//   - Settings          (the page itself: load -> edit -> save)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "./firebaseConfig";
import {
  doc,
  getDoc,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import logo from "./assets/512.png";
import { COLORS, THEME, pageBackground } from "./theme";
import { cap10Digits, formatUSPhone } from "./utils/phone";

const db = getFirestore();

function IconSave({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
      <path d="M17 21v-8H7v8" />
      <path d="M7 3v5h8" />
    </svg>
  );
}

function IconUser({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.8-4 5-6 8-6s6.2 2 8 6" />
    </svg>
  );
}

function IconPhone({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 0111.19 19 19.5 19.5 0 015 12.81 19.79 19.79 0 012.08 4.18 2 2 0 014.06 2h3a2 2 0 012 1.72c.12.9.32 1.78.59 2.63a2 2 0 01-.45 2.11L8 9.66a16 16 0 006.34 6.34l1.2-1.2a2 2 0 012.11-.45c.85.27 1.73.47 2.63.59A2 2 0 0122 16.92z" />
    </svg>
  );
}

function IconNewsletter({ className = "w-5 h-5" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6h16v12H4z" />
      <path d="M4 7l8 6 8-6" />
    </svg>
  );
}

// One labeled row in the settings form: icon + label on top,
// the actual input field underneath. Used for every editable setting.
function SettingsField({ label, icon, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2">
        <div
          className="rounded-xl border bg-white p-2"
          style={{
            color: THEME.primary,
            borderColor: THEME.border,
          }}
        >
          {icon}
        </div>

        <div className="text-sm font-extrabold" style={{ color: THEME.text }}>
          {label}
        </div>
      </div>

      <div className="mt-3">{children}</div>
    </label>
  );
}

function Settings() {
  const navigate = useNavigate();

  // Page status flags.
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The editable values on the form.
  const [email, setEmail] = useState("");           // shown read-only
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);

  // Staff-only preference: when true, the staff member's first name is
  // attached to messages they send so regular users see it under the
  // bubble instead of the generic "Staff" label. The toggle UI only
  // renders for staff (isStaff below), but the field is stored on every
  // user doc for simplicity.
  const [isStaff, setIsStaff] = useState(false);
  const [displayFirstNameInMessages, setDisplayFirstNameInMessages] =
    useState(false);

  // A snapshot of what was on the page when we loaded it. We compare
  // the current form values against this to know if there's anything
  // to save (used to disable/enable the Save button).
  const [originalData, setOriginalData] = useState(null);
  const [message, setMessage] = useState("");  // "Saved!" confirmation
  const [error, setError] = useState("");      // red error text

  const inputClassName =
    "w-full rounded-2xl border bg-white px-4 py-3 text-sm outline-none transition focus:ring-4 focus:ring-white/80";

  // When the page loads, fetch the signed-in user's profile from
  // Firestore and fill the form with it. If they aren't signed in,
  // send them back to the login page. If they're signed in but have
  // no profile yet, send them to /sign-up to finish setup.
  useEffect(() => {
    let mounted = true;

    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/", { replace: true });
        return;
      }

      try {
        setLoading(true);
        setError("");
        setEmail(user.email || "");

        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (!snap.exists()) {
          navigate("/sign-up", { replace: true });
          return;
        }

        const data = snap.data() || {};

        if (!mounted) return;

        const fn = data.firstName || "";
        const ln = data.lastName || "";
        const phone = cap10Digits(data.phoneNumber || data.phone || data.cell || "");
        const newsletter = Boolean(data.newsletterOptIn);
        const staff = data.staff === true;
        const showFirstName = data.displayFirstNameInMessages === true;

        setFirstName(fn);
        setLastName(ln);
        setPhoneNumber(phone);
        setNewsletterOptIn(newsletter);
        setIsStaff(staff);
        setDisplayFirstNameInMessages(showFirstName);

        setOriginalData({
          firstName: fn,
          lastName: ln,
          phoneNumber: phone,
          newsletterOptIn: newsletter,
          displayFirstNameInMessages: showFirstName,
        });
      } catch (err) {
        console.error("Settings: failed to load user settings", err);
        if (mounted) setError("Could not load your settings.");
      } finally {
        if (mounted) setLoading(false);
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, [navigate]);

  // True if the user has actually changed anything on the form.
  // The Save button is disabled when this is false.
  const hasChanges = useMemo(() => {
    if (!originalData) return false;

    return (
      firstName.trim() !== originalData.firstName ||
      lastName.trim() !== originalData.lastName ||
      phoneNumber !== originalData.phoneNumber ||
      newsletterOptIn !== originalData.newsletterOptIn ||
      displayFirstNameInMessages !== originalData.displayFirstNameInMessages
    );
  }, [
    firstName,
    lastName,
    phoneNumber,
    newsletterOptIn,
    displayFirstNameInMessages,
    originalData,
  ]);

  // Runs when the user submits the form (clicks Save). Writes the
  // edited values back to the user's document in Firestore. Adding
  // a new field to the form? Save it here as well.
  async function handleSave(e) {
    e.preventDefault();

    setSaving(true);
    setMessage("");
    setError("");

    try {
      const user = auth.currentUser;

      if (!user) {
        navigate("/", { replace: true });
        return;
      }

      const cleanFirstName = firstName.trim();
      const cleanLastName = lastName.trim();
      const cleanPhoneNumber = cap10Digits(phoneNumber);

      if (!cleanFirstName || !cleanLastName) {
        setError("First name and last name are required.");
        setSaving(false);
        return;
      }

      if (cleanPhoneNumber && cleanPhoneNumber.length !== 10) {
        setError("Please enter a valid 10-digit phone number or leave it blank.");
        setSaving(false);
        return;
      }

      const userRef = doc(db, "users", user.uid);

      await updateDoc(userRef, {
        firstName: cleanFirstName,
        lastName: cleanLastName,
        name: `${cleanFirstName} ${cleanLastName}`,
        phoneNumber: cleanPhoneNumber,
        phone: cleanPhoneNumber,
        cell: cleanPhoneNumber,
        newsletterOptIn,
        displayFirstNameInMessages,
        updatedAt: serverTimestamp(),
      });

      setOriginalData({
        firstName: cleanFirstName,
        lastName: cleanLastName,
        phoneNumber: cleanPhoneNumber,
        newsletterOptIn,
        displayFirstNameInMessages,
      });

      setPhoneNumber(cleanPhoneNumber);
      setMessage("Settings updated successfully.");
    } catch (err) {
      console.error("Settings: failed to save user settings", err);
      setError("Could not save your settings. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={pageBackground}
      >
        <div
          className="rounded-2xl border px-5 py-4 bg-white shadow-lg"
          style={{ borderColor: THEME.border }}
        >
          <div className="text-base font-extrabold" style={{ color: THEME.text }}>
            Loading…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative" style={pageBackground}>
      {/* --- Page header: logo on left, Home button on right --- */}
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to home"
            type="button"
          >
            <img src={logo} alt="Logo" className="w-13 h-12 object-contain" />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Settings
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate("/home")}
            className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
            type="button"
          >
            Home
          </button>
        </div>
      </header>

      {/* --- Main card with the page heading and the form below. --- */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        <section
          className="backdrop-blur border rounded-3xl p-5 sm:p-6 lg:p-8"
          style={{
            backgroundColor: THEME.cardBg,
            borderColor: THEME.border,
            boxShadow: THEME.shadow,
          }}
        >
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
            <div>
              <div
                className="inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold"
                style={{
                  color: COLORS.brown,
                }}
              >
                Account Settings
              </div>

              <h1
                className="text-3xl sm:text-4xl font-extrabold mt-4"
                style={{ color: THEME.text }}
              >
                Update Your Information
              </h1>

              <p
                className="mt-2 max-w-2xl"
                style={{ color: "rgba(31,56,0,0.66)" }}
              >
                Change your name, phone number, and newsletter preference.
              </p>
            </div>

            <div
              className="rounded-2xl border bg-white px-4 py-3 lg:min-w-[280px]"
              style={{
                borderColor: THEME.border,
                boxShadow: "0 10px 22px rgba(31,56,0,0.08)",
              }}
            >
              <div
                className="text-xs font-semibold"
                style={{ color: "rgba(31,56,0,0.52)" }}
              >
                Signed in as
              </div>
              <div
                className="text-sm font-extrabold break-all mt-1"
                style={{ color: THEME.text }}
              >
                {email || "—"}
              </div>
            </div>
          </div>

          {error ? (
            <div className="mt-6 rounded-2xl border px-4 py-3 text-sm font-bold">
              <div style={{ color: "#991b1b" }}>{error}</div>
            </div>
          ) : null}

          {message ? (
            <div
              className="mt-6 rounded-2xl border px-4 py-3 text-sm font-bold"
              style={{
                backgroundColor: COLORS.greenLight,
                borderColor: THEME.border,
                color: THEME.text,
              }}
            >
              {message}
            </div>
          ) : null}

          {/* The actual form. Each <SettingsField> is one labeled row.
              To add a new editable setting, add another SettingsField
              here and remember to save it in handleSave() above. */}
          <form onSubmit={handleSave} className="mt-7 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SettingsField label="First Name" icon={<IconUser className="w-4 h-4" />}>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputClassName}
                  style={{
                    borderColor: THEME.border,
                    color: THEME.text,
                  }}
                  placeholder="First name"
                />
              </SettingsField>

              <SettingsField label="Last Name" icon={<IconUser className="w-4 h-4" />}>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputClassName}
                  style={{
                    borderColor: THEME.border,
                    color: THEME.text,
                  }}
                  placeholder="Last name"
                />
              </SettingsField>
            </div>

            <SettingsField label="Phone Number" icon={<IconPhone className="w-4 h-4" />}>
              <input
                type="tel"
                inputMode="tel"
                value={formatUSPhone(phoneNumber)}
                onChange={(e) => setPhoneNumber(cap10Digits(e.target.value))}
                className={inputClassName}
                style={{
                  borderColor: THEME.border,
                  color: THEME.text,
                }}
                placeholder="(555) 123-4567"
              />
            </SettingsField>

            <div
              className="rounded-2xl border p-5"
              style={{
                backgroundColor: THEME.cardBgSoft,
                borderColor: THEME.border,
              }}
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newsletterOptIn}
                  onChange={(e) => setNewsletterOptIn(e.target.checked)}
                  className="mt-1 h-5 w-5"
                  style={{ accentColor: THEME.primary }}
                />

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div
                      className="rounded-xl border bg-white p-2"
                      style={{
                        color: COLORS.aquaDark,
                        borderColor: THEME.border,
                      }}
                    >
                      <IconNewsletter className="w-4 h-4" />
                    </div>

                    <div
                      className="text-sm font-extrabold"
                      style={{ color: THEME.text }}
                    >
                      Newsletter Opt-In
                    </div>
                  </div>

                  <p
                    className="text-sm mt-3"
                    style={{ color: "rgba(31,56,0,0.66)" }}
                  >
                    Receive updates, announcements, and important information.
                  </p>
                </div>
              </label>
            </div>

            {isStaff ? (
              <div
                className="rounded-2xl border p-5"
                style={{
                  backgroundColor: THEME.cardBgSoft,
                  borderColor: THEME.border,
                }}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={displayFirstNameInMessages}
                    onChange={(e) =>
                      setDisplayFirstNameInMessages(e.target.checked)
                    }
                    className="mt-1 h-5 w-5"
                    style={{ accentColor: THEME.primary }}
                  />

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div
                        className="rounded-xl border bg-white p-2"
                        style={{
                          color: COLORS.aquaDark,
                          borderColor: THEME.border,
                        }}
                      >
                        <IconUser className="w-4 h-4" />
                      </div>

                      <div
                        className="text-sm font-extrabold"
                        style={{ color: THEME.text }}
                      >
                        Show My First Name In Messages
                      </div>
                    </div>

                    <p
                      className="text-sm mt-3"
                      style={{ color: "rgba(31,56,0,0.66)" }}
                    >
                      When on, users see your first name (for example,
                      "{firstName || "Sarah"}") under messages you send instead
                      of the generic "Staff" label. Only applies to new
                      messages you send after turning this on.
                    </p>
                  </div>
                </label>
              </div>
            ) : null}

            <div className="flex justify-end pt-2">
              <button
                onClick={() => navigate("/home")}
                type="submit"
                disabled={saving || !hasChanges}
                className="rounded-2xl px-5 py-3 text-sm font-extrabold text-white transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:opacity-90"
                style={{
                  backgroundColor: THEME.primary,
                  boxShadow: "0 12px 24px rgba(64,115,0,0.18)",
                }}
              >
                <IconSave className="w-4 h-4" />
                {saving ? "Saving..." : "Save & Exit"}
              </button>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => navigate("/home")}
                type="submit"
                className="rounded-2xl px-5 py-3 text-sm font-extrabold text-white transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:opacity-90"
                style={{
                  backgroundColor: THEME.primary,
                  boxShadow: "0 12px 24px rgba(64,115,0,0.18)",
                }}
              >
                {"Exit without saving"}
              </button>
            </div>

          </form>
        </section>
      </main>
    </div>
  );
}

export default Settings;